// SS PWA — components/diagnostics/SyntheticTestPanel.tsx — 2026-06-03
//
// Operator-only synthetic test launcher. Buttons: run synthetic call, sms,
// push, workflow_trigger. Shows the returned traceId + status.

import { useState } from 'react';
import {
  postSynthetic,
  type SyntheticRecord,
  DiagnosticsApiError,
} from '@/lib/api/diagnostics';

const TYPES: Array<{ key: 'call' | 'sms' | 'push' | 'workflow_trigger'; label: string }> = [
  { key: 'push', label: 'Push' },
  { key: 'workflow_trigger', label: 'Workflow probe' },
  { key: 'call', label: 'Call (metadata)' },
  { key: 'sms', label: 'SMS (metadata)' },
];

export function SyntheticTestPanel({ slug }: { slug: string }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<SyntheticRecord | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run(type: 'call' | 'sms' | 'push' | 'workflow_trigger') {
    if (!slug) return;
    setBusy(type);
    setError(null);
    try {
      const r = await postSynthetic(slug, type);
      setResult(r.record);
    } catch (err) {
      if (err instanceof DiagnosticsApiError) {
        setError(`API ${err.status}`);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-3 dark:border-[#1F2A37] dark:bg-[#0F172A]">
      <div className="mb-2 text-sm font-semibold text-ink-900 dark:text-white">Synthetic tests</div>
      <div className="flex flex-wrap gap-2">
        {TYPES.map((t) => (
          <button
            key={t.key}
            type="button"
            disabled={!slug || busy !== null}
            onClick={() => void run(t.key)}
            className="rounded-md border border-ink-300 bg-white px-2 py-1 text-xs text-ink-800 hover:bg-ink-50 disabled:opacity-50 dark:border-[#334155] dark:bg-[#0F172A] dark:text-white dark:hover:bg-[#1F2A37]"
          >
            {busy === t.key ? 'Running…' : t.label}
          </button>
        ))}
      </div>
      {result ? (
        <div className="mt-2 rounded-md border border-ink-100 bg-ink-50 p-2 text-xs dark:border-[#1F2A37] dark:bg-[#1F2A37]">
          <div className="font-mono text-ink-900 dark:text-white">{result.trace_id}</div>
          <div className="mt-1 text-ink-600 dark:text-[#94A3B8]">
            type={result.type} · status={result.status}
          </div>
        </div>
      ) : null}
      {error ? (
        <div className="mt-2 text-xs text-red-500">{error}</div>
      ) : null}
    </div>
  );
}
